import { fetchAllAdminUsers } from '@actions/adminUsers';
import { fetchChallengeGroup } from '@actions/challengeGroups';
import {
    createChallenge,
    CreateChallengeRequest,
    fetchChallengeCalendarAdminOptions,
} from '@actions/challenges';
import { fetchAllChallengeTags } from '@actions/challengeTags';
import { fetchAllBusinessTags } from '@actions/challengeTags/fetchAllBusinessTags';
import { getAdminUsers } from '@selectors/adminUsers';
import {
    getCalendarAssessors,
    getCalendarPitchPresentationAdmins,
    getCalendarWorkshopAdmins,
    getChallengesIsPosting,
    getChallengesPostError,
    getChallengesPostSuccess,
} from '@selectors/challenges';
import { getBusinessTags, getChallengeTags } from '@selectors/challengeTags';
import dayjs from 'dayjs';
import useForm from '@components/form/hooks/useForm';
import usePrevious from 'lib/src/hooks/usePrevious';
import { site as siteEnum } from 'lib/src/shared/enums/site';
import { DropdownOption } from 'lib/src/types/shared/DropdownOption';
import { useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { AdminUser } from 'src/types/shared/AdminUser';
import { Tag } from 'src/types/shared/Tags';
import { ChallengeDescriptionSectionRequest } from 'src/types/shared/Challenge';
import { getChallengeGroup } from '@selectors/challengeGroups';
import { RootState } from '@reducers/index';
import useSite from 'lib/src/hooks/useSite';
import { ChallengeCalendarAdminOption } from 'src/types/shared/User';

const useCreateChallenge = (challengeGroupID: number) => {
    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        if (challengeGroupID) {
            batch(() => {
                dispatch(fetchChallengeGroup(challengeGroupID));
                dispatch(fetchChallengeCalendarAdminOptions(challengeGroupID));
            });
        }
    }, [dispatch, challengeGroupID]);

    useEffect(() => {
        batch(() => {
            dispatch(fetchAllChallengeTags());
            dispatch(fetchAllBusinessTags());
            dispatch(fetchAllAdminUsers());
        });
    }, [dispatch]);

    const challengeTags = useSelector(getChallengeTags);
    const businessTags = useSelector(getBusinessTags);
    const postSuccess = useSelector(getChallengesPostSuccess);
    const isPosting = useSelector(getChallengesIsPosting);
    const error = useSelector(getChallengesPostError);
    const challengeGroup = useSelector((state: RootState) =>
        getChallengeGroup(state, challengeGroupID),
    );
    const groupSite = challengeGroup?.site;
    const userSite = useSite();
    const admins = useSelector(getAdminUsers);
    const workshopAdmins = useSelector(getCalendarWorkshopAdmins) || {};
    const assessors = useSelector(getCalendarAssessors) || {};
    const pitchPresentationAdmins = useSelector(getCalendarPitchPresentationAdmins) || {};

    /// Default dates
    const workshopDate = dayjs();
    const startDate = dayjs(workshopDate).add(1, 'days');
    const endDate = dayjs(startDate).add(7, 'days');
    const gracePeriod = 1;
    const assessmentStartDate = dayjs(endDate).add(gracePeriod + 1, 'days');
    const responseDate = dayjs(assessmentStartDate).add(10, 'days');
    const pitchPresentationDate = dayjs(responseDate).add(1, 'days');

    const initialData = {
        challengeGroupID,
        name: '',
        workshopDate: workshopDate.format(),
        workshopAdminIDs: [],
        startDate: startDate.format(),
        endDate: endDate.format(),
        gracePeriod,
        background: '',
        imageS3Key: [] as string[],
        partnerLogoS3Key: [] as string[],
        partnerSecondLogoS3Key: [] as string[],
        partnerThirdLogoS3Key: [] as string[],
        summaryImage1S3Key: [] as string[],
        summaryImage2S3Key: [] as string[],
        assessmentStartDate: assessmentStartDate.format(),
        responseDate: responseDate.format(),
        assessorIDs: [],
        pitchPresentationDate: pitchPresentationDate.format(),
        pitchPresentationAdminIDs: [],
        challengeLeadID: null,
        tagIDs: [],
        businessTagIDs: [],
        site: groupSite ?? siteEnum.IX,
        descriptionSections: [] as ChallengeDescriptionSectionRequest[],
        awardDescription: '',
    };
    const [formState, handleChange] = useForm(initialData);
    const prevProps = usePrevious({ postSuccess, formState, site: groupSite, userSite });

    useEffect(() => {
        if (!!groupSite && !prevProps.site) {
            handleChange('site', groupSite);
        }
        if (
            !!userSite &&
            !prevProps.userSite &&
            userSite !== siteEnum.ALL &&
            groupSite !== userSite
        ) {
            handleChange('site', userSite);
        }
    }, [groupSite, prevProps, handleChange, userSite]);

    const handleSubmit = () => {
        console.log(formState);
        const request: CreateChallengeRequest = {
            ...formState,
            responseDate: dayjs(formState.responseDate).format('YYYY-MM-DD'),
            startDate: dayjs(formState.startDate).format('YYYY-MM-DD'),
            endDate: dayjs(formState.endDate).format('YYYY-MM-DD'),
            workshopDate: dayjs(formState.workshopDate).format('YYYY-MM-DD'),
            assessmentStartDate: dayjs(formState.assessmentStartDate).format('YYYY-MM-DD'),
            pitchPresentationDate: dayjs(formState.pitchPresentationDate).format('YYYY-MM-DD'),
            imageS3Key: formState.imageS3Key[0] || null,
            partnerLogoS3Key: formState.partnerLogoS3Key[0] || null,
            partnerSecondLogoS3Key: formState.partnerSecondLogoS3Key[0] || null,
            partnerThirdLogoS3Key: formState.partnerThirdLogoS3Key[0] || null,
            summaryImage1S3Key: formState.summaryImage1S3Key[0] || null,
            summaryImage2S3Key: formState.summaryImage2S3Key[0] || null,
        };
        dispatch(createChallenge(request));
    };

    useEffect(() => {
        if (postSuccess && !prevProps.postSuccess) {
            history.push(`/challenge-groups/${challengeGroupID}/challenges`);
        }
    }, [postSuccess, prevProps.postSuccess, history, challengeGroupID]);

    const tagOptions = Object.values(challengeTags).map(getTagOption);
    const businessTagOptions = Object.values(businessTags).map(getTagOption);
    const filteredAdmins = Object.values(admins).filter(
        admin => admin.site === siteEnum.ALL || admin.site === formState.site,
    );
    const adminOptions = filteredAdmins.map(getUserOption);
    const assessorOptions = Object.values(assessors)
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(getUserOption);
    const workshopAdminOptions = Object.values(workshopAdmins)
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(getUserOption);
    const pitchPresentationAdminOptions = Object.values(pitchPresentationAdmins)
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map(getUserOption);

    const siteOptions: DropdownOption<siteEnum>[] = [
        { label: 'All', value: siteEnum.ALL },
        { label: 'IX', value: siteEnum.IX },
        { label: 'GA', value: siteEnum.GA },
    ].filter(option => userSite === siteEnum.ALL || option.value === userSite);
    const shouldShowSiteOptions = groupSite === siteEnum.ALL && userSite === siteEnum.ALL;

    return {
        formState,
        handleChange,
        handleSubmit,
        isPosting,
        error,
        tagOptions,
        businessTagOptions,
        adminOptions,
        assessorOptions,
        workshopAdminOptions,
        pitchPresentationAdminOptions,
        siteOptions,
        shouldShowSiteOptions,
    };
};

const getTagOption = (tag: Tag): DropdownOption<number> => ({ value: tag.id, label: tag.name });
const getUserOption = (user: AdminUser | ChallengeCalendarAdminOption): DropdownOption<number> => ({
    value: user.id,
    label: user.firstName + ' ' + user.lastName,
});
export default useCreateChallenge;
